import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahgj6joh653ivfvnulg";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubmitForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorText, setErrorText] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    payload.delete("website_alt");

    setStatus("submitting");
    setErrorText("");

    try {
      const body = new URLSearchParams();
      payload.forEach((value, key) => {
        if (typeof value === "string") {
          body.append(key, value);
        }
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;

      if (!response.ok || code !== "OK") {
        setStatus("error");
        setErrorText(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setErrorText("网络异常，提交失败，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-8 text-center">
        <i className="ri-checkbox-circle-line text-3xl text-secondary-600" />
        <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">已收到你的反馈</h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
          我们会在核对后更新对应事件的证据链，如需进一步确认会通过你留下的邮箱联系。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-secondary-400 px-4 py-2 font-label text-sm text-secondary-800 transition-colors hover:bg-secondary-100 cursor-pointer"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-300 bg-background-100 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="font-label text-xs font-semibold text-foreground-700">
            称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            placeholder="如何称呼你"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="email" className="font-label text-xs font-semibold text-foreground-700">
            邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="用于回复与核实"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="clue_type" className="font-label text-xs font-semibold text-foreground-700">
            反馈类型
          </label>
          <select
            id="clue_type"
            name="clue_type"
            defaultValue="提供新线索"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
          >
            <option>提供新线索</option>
            <option>信息更正</option>
            <option>补充原始素材</option>
            <option>其他建议</option>
          </select>
        </div>
        <div>
          <label htmlFor="topic_link" className="font-label text-xs font-semibold text-foreground-700">
            关联事件或链接
          </label>
          <input
            id="topic_link"
            name="topic_link"
            type="text"
            placeholder="事件标题或原始链接"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="message" className="font-label text-xs font-semibold text-foreground-700">
            情况说明 <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="简要说明你知道的情况、素材来源与时间，最多500字"
            className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-alt-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && errorText && (
        <p className="mt-5 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-3 py-2.5 text-sm text-primary-900">
          <i className="ri-error-warning-line mt-0.5" />
          {errorText}
        </p>
      )}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-label text-xs text-foreground-500">
          我们仅将信息用于核查与更正，不会公开你的联系方式。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin" /> 提交中
            </>
          ) : (
            <>
              提交反馈 <i className="ri-send-plane-line" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}